/**
 * Premium chain — the three sequential multiplications that turn an
 * equivalency + rate into the final SIR-adjusted premium quoted by
 * Underwriting:
 *
 *   Unadj. Premium    = Equivalency  × Rate
 *   Mat. Adj. Premium = Unadj. Prem.  × maturityCredit
 *   SIR Adj. Premium  = Mat. Adj.    × (1 − sirCredit)
 *
 * Each step propagates `null` cleanly: a missing rate (unknown
 * state) or missing SIR credit (off-tier retention) cascades to "—"
 * in the UI instead of poisoning the downstream values with `NaN`.
 */

/** Step 1 — multiply Equivalency by the actuarial Rate. */
export function computeUnadjPremium(equivalency: number, rate: number | null): number | null {
    if (rate === null) return null;
    return equivalency * rate;
}

/**
 * Step 2 — apply the maturity credit.
 *
 *   Mat. Adj. Premium = Unadj. Premium × maturityCredit
 *
 * `maturityCredit` is the **fraction RETAINED**, NOT a discount.
 * Default 1.0 means "keep 100 % of unadjusted premium" (no maturity
 * adjustment). Lower values trim the premium for less-mature
 * accounts. The Schedule UI surfaces 43 % / 80 % / 96 % / 100 % +
 * custom; the multiplication direction here matches the spreadsheet
 * column `AB = Z × AA` exactly.
 *
 * If Underwriting later confirms credit semantics actually mean
 * "discount", flip to `unadjPremium × (1 - maturityCredit)` and
 * update the test fixtures — the call sites won't change.
 */
export function computeMatAdjPremium(unadjPremium: number | null, maturityCredit: number): number | null {
    if (unadjPremium === null) return null;
    return unadjPremium * maturityCredit;
}

/** Step 3 — drop the SIR credit off the maturity-adjusted premium. */
export function computeSirAdjPremium(matAdjPremium: number | null, sirCredit: number | null): number | null {
    if (matAdjPremium === null || sirCredit === null) return null;
    return matAdjPremium * (1 - sirCredit);
}
